import { useCallback, useEffect, useState } from 'react';
import {
  marketingApi,
  type MarketingCampaign,
  type MarketingSegment,
  type SharedTemplate,
} from '@/api/marketing.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormGrid } from '@/components/ui/FormGrid';
import { Select } from '@/components/ui/Select';
import { showApiError } from '@/utils/apiError';
import { MARKETING_CHANNELS } from './constants';
import { MarketingStatusBadge } from './components/MarketingStatusBadge';
import { MarketingEmptyState } from './components/MarketingEmptyState';
import { ScheduleCampaignModal } from './components/ScheduleCampaignModal';
import { CampaignPerformancePanel } from './components/CampaignPerformancePanel';
import { CampaignAbTestsPanel } from './components/CampaignAbTestsPanel';
import { CampaignRowActions } from './components/CampaignRowActions';
import { TablePagination } from '@/components/ui/TablePagination';
import { paginateClientList, usePagination } from '@/hooks/usePagination';

import { notify } from '@/core/toast/notify';
export function MarketingCampaignsPage() {
  const pagination = usePagination();
  const [campaigns, setCampaigns] = useState<MarketingCampaign[]>([]);
  const [segments, setSegments] = useState<MarketingSegment[]>([]);
  const [templates, setTemplates] = useState<SharedTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<MarketingCampaign | null>(null);
  const [perf, setPerf] = useState<Record<string, number> | null>(null);
  const [perfLoading, setPerfLoading] = useState(false);
  const [attribution, setAttribution] = useState<Record<string, unknown> | null>(null);
  const [scheduleTarget, setScheduleTarget] = useState<MarketingCampaign | null>(null);

  const [name, setName] = useState('');
  const [channel, setChannel] = useState('EMAIL');
  const [segmentId, setSegmentId] = useState<number | ''>('');
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, t] = await Promise.all([
        marketingApi.listCampaigns(),
        marketingApi.listSegments(),
        marketingApi.listTemplates(),
      ]);
      setCampaigns(c);
      pagination.applyClientCount(c.length);
      setSegments(s);
      setTemplates(t);
    } catch (err) {
      showApiError(err, 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- client-side pagination; fetch once
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!name.trim()) {
      notify.error('Enter a campaign name');
      return;
    }
    if (segmentId === '') {
      notify.error('Select an audience segment');
      return;
    }
    setSaving(true);
    try {
      await marketingApi.createCampaign({
        campaignName: name.trim(),
        channel,
        segmentId: Number(segmentId),
        sharedTemplateId: templateId === '' ? undefined : Number(templateId),
        lifecycleStatus: 'DRAFT',
      });
      notify.success('Draft campaign created');
      setName('');
      setSegmentId('');
      setTemplateId('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create campaign');
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (fn: () => Promise<void>, success: string) => {
    try {
      await fn();
      notify.success(success);
      load();
      if (selected) {
        const updated = await marketingApi.getCampaign(selected.id);
        setSelected(updated);
      }
    } catch (err) {
      showApiError(err, 'Action failed');
    }
  };

  const showPerf = async (c: MarketingCampaign) => {
    setSelected(c);
    setAttribution(null);
    setPerfLoading(true);
    try {
      setPerf(await marketingApi.getPerformance(c.id));
    } catch (err) {
      showApiError(err, 'Failed to load performance');
      setPerf(null);
    } finally {
      setPerfLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Campaigns"
        description="Create, approve, schedule, and launch multi-channel campaigns with tracked delivery."
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Marketing', to: '/marketing' },
          { label: 'Campaigns' },
        ]}
      />

      <section className="card p-5">
        <h2 className="text-sm font-semibold text-slate-800 mb-3">New campaign</h2>
        <FormGrid columns={4} className="items-end">
          <Input label="Name" placeholder="Spring promo" value={name} onChange={(e) => setName(e.target.value)} />
          <div>
            <Select
              label="Channel"
              options={MARKETING_CHANNELS.map((c) => ({ value: c, label: c }))}
              value={channel}
              onChange={setChannel}
            />
          </div>
          <div>
            <Select
              label="Audience"
              placeholder="Select segment"
              options={segments.map((s) => ({
                value: String(s.id),
                label: `${s.segmentName} (${s.estimatedCount.toLocaleString()})`,
              }))}
              value={segmentId === '' ? '' : String(segmentId)}
              onChange={(v) => setSegmentId(v ? Number(v) : '')}
            />
          </div>
          <div>
            <Select
              label="Template"
              placeholder="Optional"
              options={templates.map((t) => ({ value: String(t.id), label: t.templateName }))}
              value={templateId === '' ? '' : String(templateId)}
              onChange={(v) => setTemplateId(v ? Number(v) : '')}
            />
          </div>
          <Button loading={saving} onClick={() => void create()}>
            Create draft
          </Button>
        </FormGrid>
      </section>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card overflow-hidden">
          {loading ? (
            <p className="p-6 text-sm text-slate-500">Loading campaigns…</p>
          ) : campaigns.length === 0 ? (
            <MarketingEmptyState
              title="No campaigns yet"
              description="Create a draft with an audience segment and template, then submit for approval."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-slate-600">
                  <tr>
                    <th className="p-3 font-medium">Campaign</th>
                    <th className="p-3 font-medium">Channel</th>
                    <th className="p-3 font-medium">Status</th>
                    <th className="p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginateClientList(campaigns, pagination.page, pagination.size).map((c) => (
                    <tr
                      key={c.id}
                      className={`border-t border-slate-100 hover:bg-slate-50/50 ${selected?.id === c.id ? 'bg-violet-50/40' : ''}`}
                    >
                      <td className="p-3 font-medium text-slate-800">{c.campaignName}</td>
                      <td className="p-3 text-slate-600">{c.channel}</td>
                      <td className="p-3">
                        <MarketingStatusBadge status={c.lifecycleStatus} />
                      </td>
                      <td className="p-3">
                        <CampaignRowActions
                          campaign={c}
                          handlers={{
                            onStats: showPerf,
                            onClone: (id) => runAction(() => marketingApi.cloneCampaign(id).then(() => {}), 'Campaign cloned'),
                            onSubmit: (id) => runAction(() => marketingApi.submitApproval(id).then(() => {}), 'Submitted for approval'),
                            onApprove: (id) => runAction(() => marketingApi.approveCampaign(id).then(() => {}), 'Campaign approved'),
                            onSchedule: setScheduleTarget,
                            onLaunch: (id) => runAction(() => marketingApi.startCampaign(id).then(() => {}), 'Campaign queued for send'),
                            onComplete: (id) => runAction(() => marketingApi.completeCampaign(id).then(() => {}), 'Campaign completed'),
                            onArchive: (id) => runAction(() => marketingApi.archiveCampaign(id).then(() => {}), 'Campaign archived'),
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                totalElements={pagination.totalElements}
                onPageChange={pagination.setPage}
                loading={loading}
                hasRows={campaigns.length > 0}
              />
            </div>
          )}
        </div>

        <div className="space-y-4">
          <CampaignPerformancePanel
            campaign={selected}
            performance={perf}
            loading={perfLoading}
            onLoadAttribution={async (id) => {
              try {
                setAttribution(await marketingApi.getAttribution(id));
              } catch (err) {
                showApiError(err, 'Attribution failed');
              }
            }}
            attribution={attribution}
          />
          <CampaignAbTestsPanel campaign={selected} />
        </div>
      </div>

      {scheduleTarget && (
        <ScheduleCampaignModal
          open={!!scheduleTarget}
          campaignName={scheduleTarget.campaignName}
          onClose={() => setScheduleTarget(null)}
          onConfirm={async (scheduledAt) => {
            await marketingApi.scheduleCampaign(scheduleTarget.id, scheduledAt);
            notify.success('Campaign scheduled');
            setScheduleTarget(null);
            load();
          }}
        />
      )}
    </div>
  );
}

